/**
 * Что держит работу (ВП-04): комплекты ПСД и вопросы к проекту, привязанные к работе графика.
 * Красным — комплект не получен или вопрос открыт, а работа должна начаться раньше ожидаемой даты.
 */
import { QUESTION_STATUSES, type QuestionSummary, type SetSummary } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button, IconButton } from '../../ui/button';
import { Badge, toast } from '../../ui/feedback';
import { Select } from '../../ui/form';

interface Holds {
  inspection: { id: string | null; number: string | null; status: string } | null;
  sets: Array<{
    id: string;
    code: string;
    name: string;
    plannedDate: string | null;
    receivedDate: string | null;
    late: boolean;
  }>;
  questions: Array<{
    id: string;
    number: string;
    subject: string;
    status: keyof typeof QUESTION_STATUSES;
    dueDate: string | null;
    open: boolean;
    late: boolean;
  }>;
  canSets: boolean;
  canQuestions: boolean;
}
const dd = (s: string | null) => (s ? s.slice(0, 10).split('-').reverse().join('.') : '—');

export function ActivityHolds({
  activityId,
  projectId,
  canEdit,
}: {
  activityId: string;
  projectId: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const key = ['activity-holds', activityId];
  const q = useQuery({ queryKey: key, queryFn: () => api<Holds>(`/activities/${activityId}/holds`) });
  const sets = useQuery({
    queryKey: ['psd-sets-for', projectId],
    queryFn: () => api<SetSummary[]>('/psd/sets', { query: { projectId } }),
    enabled: canEdit && Boolean(q.data?.canSets),
    retry: false,
  });
  const questions = useQuery({
    queryKey: ['questions-for', projectId],
    queryFn: () => api<QuestionSummary[]>('/supervision/questions', { query: { projectId, status: 'open' } }),
    enabled: canEdit && Boolean(q.data?.canQuestions),
    retry: false,
  });
  const [pick, setPick] = useState('');
  const m = useMutation({
    mutationFn: (b: { type: 'psd_set' | 'psd_question'; targetId: string; remove?: boolean }) =>
      api<Holds>(`/activities/${activityId}/holds`, { body: b }),
    onSuccess: (h) => {
      qc.setQueryData(key, h);
      qc.invalidateQueries({ queryKey: ['schedule-holds'] });
      setPick('');
    },
    onError: (e) => toast(e.message, 'danger'),
  });
  const h = q.data;
  if (!h || (!h.canSets && !h.canQuestions)) return null;
  const linked = new Set([...h.sets.map((s) => s.id), ...h.questions.map((x) => x.id)]);
  const options = [
    ...(sets.data ?? [])
      .filter((s) => !linked.has(s.id))
      .map((s) => ({ v: `psd_set:${s.id}`, t: `Комплект ${s.code} ${s.name}` })),
    ...(questions.data ?? [])
      .filter((x) => !linked.has(x.id))
      .map((x) => ({ v: `psd_question:${x.id}`, t: `${x.number} ${x.subject}` })),
  ];
  const row = 'flex items-center justify-between gap-2 py-1.5';
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[14px] font-semibold">ПСД и вопросы под работу</h3>
      {h.inspection ? (
        <p className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-[13px] text-danger">
          Начинать нельзя: акт освидетельствования скрытых работ{' '}
          {h.inspection.id ? (
            <>
              по вызову{' '}
              <Link
                to="/supervision/inspections/$inspectionId"
                params={{ inspectionId: h.inspection.id }}
                className="inline-flex min-h-11 items-center font-medium underline md:min-h-0"
              >
                {h.inspection.number}
              </Link>{' '}
            </>
          ) : null}
          ещё не подписан.
        </p>
      ) : null}
      {h.sets.length || h.questions.length ? (
        <ul className="divide-y divide-line text-[14px]">
          {h.sets.map((s) => (
            <li key={s.id} className={row}>
              <span className={clsx('min-w-0', s.late && 'text-danger')}>
                <Link
                  to="/psd/sets/$setId"
                  params={{ setId: s.id }}
                  className="inline-flex min-h-11 items-center hover:underline md:min-h-0"
                >
                  Комплект {s.code} {s.name}
                </Link>
                <span className="ml-2 text-[12px]">
                  {s.receivedDate
                    ? `получен ${dd(s.receivedDate)}`
                    : `ожидается ${dd(s.plannedDate)}${s.late ? ' — позже начала работы' : ''}`}
                </span>
              </span>
              {canEdit ? (
                <IconButton
                  label="Отвязать комплект"
                  onClick={() => m.mutate({ type: 'psd_set', targetId: s.id, remove: true })}
                  className="size-11 md:size-9"
                >
                  <Trash2 className="size-4" />
                </IconButton>
              ) : null}
            </li>
          ))}
          {h.questions.map((x) => (
            <li key={x.id} className={row}>
              <span className={clsx('min-w-0', x.late && 'text-danger')}>
                <Link
                  to="/supervision/questions/$questionId"
                  params={{ questionId: x.id }}
                  className="inline-flex min-h-11 items-center hover:underline md:min-h-0"
                >
                  {x.number} {x.subject}
                </Link>
                <span className="ml-2 text-[12px]">
                  {x.open ? `ответ к ${dd(x.dueDate)}${x.late ? ' — позже начала работы' : ''}` : ''}
                </span>
              </span>
              <span className="flex items-center gap-1">
                <Badge tone={x.open ? 'warning' : 'success'}>{QUESTION_STATUSES[x.status] ?? x.status}</Badge>
                {canEdit ? (
                  <IconButton
                    label="Отвязать вопрос"
                    onClick={() => m.mutate({ type: 'psd_question', targetId: x.id, remove: true })}
                    className="size-11 md:size-9"
                  >
                    <Trash2 className="size-4" />
                  </IconButton>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[13px] text-ink-2">Ничего не привязано.</p>
      )}
      {canEdit && options.length ? (
        <div className="flex gap-2">
          <Select
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            className="min-w-0 flex-1 max-md:h-11"
            aria-label="Привязать комплект ПСД или вопрос"
          >
            <option value="">Выберите комплект или вопрос…</option>
            {options.map((o) => (
              <option key={o.v} value={o.v}>
                {o.t}
              </option>
            ))}
          </Select>
          <Button
            disabled={!pick}
            onClick={() => {
              const [type, targetId] = pick.split(':') as ['psd_set' | 'psd_question', string];
              m.mutate({ type, targetId });
            }}
            className="max-md:h-11"
          >
            Привязать
          </Button>
        </div>
      ) : null}
    </section>
  );
}

interface ScheduleHold {
  activityId: string;
  code: string;
  name: string;
  start: string;
  kind: 'psd_set' | 'psd_question' | 'inspection';
  targetId: string;
  title: string;
  expected: string | null;
  late: boolean;
}

/** Над диаграммой: работы, которые ждут ПСД или ответа на вопрос позже своего начала, или акта освидетельствования */
export function ScheduleHolds({
  scheduleId,
  revision,
  onOpen,
}: {
  scheduleId: string;
  revision: number;
  onOpen: (activityId: string) => void;
}) {
  const [all, setAll] = useState(false);
  const q = useQuery({
    queryKey: ['schedule-holds', scheduleId, revision],
    queryFn: () => api<ScheduleHold[]>(`/schedules/${scheduleId}/holds`),
    retry: false,
  });
  const late = (q.data ?? []).filter((x) => x.late);
  if (!late.length) return null;
  const shown = all ? late : late.slice(0, 5);
  return (
    <section className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2.5 text-[13px]">
      <b>Работы ждут ПСД, ответа на вопрос или акта освидетельствования: {late.length}</b>
      <ul className="mt-1 flex flex-col">
        {shown.map((x) => (
          <li key={`${x.activityId}|${x.targetId}`}>
            <button
              type="button"
              onClick={() => onOpen(x.activityId)}
              className="min-h-11 text-left hover:underline md:min-h-0"
            >
              {x.code} {x.name} — начало {dd(x.start)};{' '}
              {x.kind === 'inspection'
                ? x.title
                : `${x.kind === 'psd_set' ? 'комплект' : 'вопрос'} ${x.title}, ${x.kind === 'psd_set' ? 'ожидается' : 'ответ к'} ${dd(x.expected)}`}
            </button>
          </li>
        ))}
      </ul>
      {late.length > 5 ? (
        <Button size="sm" variant="ghost" onClick={() => setAll(!all)} className="max-md:h-11">
          {all ? 'Свернуть' : `Показать все (${late.length})`}
        </Button>
      ) : null}
    </section>
  );
}
